/**
 * Audit + Email Sharing Types
 *
 * Part C5 of the Admin Communications Hub plan - EAC-style email privacy
 * + cross-rep audit log.
 */
import { z } from 'zod';
import { RoleSchema } from './features.js';
// =============================================================================
// Email sharing mode (per-user)
// =============================================================================
export const EmailSharingModeSchema = z.enum([
    'private',
    'share_with_admins',
    'share_with_everyone',
]);
export const EmailSharingModeResponseSchema = z.object({
    mode: EmailSharingModeSchema,
});
export const UpdateEmailSharingModeRequestSchema = z.object({
    mode: EmailSharingModeSchema,
});
export const MeetingVisibilityPolicySchema = z.enum([
    'user_choice',
    'admin_mandatory',
]);
export const MeetingVisibilityPolicyResponseSchema = z.object({
    policy: MeetingVisibilityPolicySchema,
});
export const UpdateMeetingVisibilityPolicyRequestSchema = z.object({
    policy: MeetingVisibilityPolicySchema,
});
// =============================================================================
// Email access type (matches DB CHECK constraint)
// =============================================================================
export const EmailAccessTypeSchema = z.enum([
    'metric',
    'list',
    'thread',
    'body',
    'audit_view',
    'break_glass',
]);
// =============================================================================
// Email access audit row - returned to UI
// =============================================================================
export const EmailAccessAuditRowSchema = z.object({
    id: z.string(),
    actorUserId: z.string().uuid(),
    actorName: z.string().nullable(),
    actorRole: z.string().nullable(),
    actorAvatarFileId: z.string().nullable().optional(),
    targetUserId: z.string().uuid(),
    targetName: z.string().nullable().optional(),
    targetRole: z.string().nullable().optional(),
    emailId: z.string().nullable(),
    threadId: z.string().nullable(),
    accessType: EmailAccessTypeSchema,
    accessedAt: z.string(),
});
// =============================================================================
// Listing responses
// =============================================================================
export const EmailAccessAuditListResponseSchema = z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
    entries: z.array(EmailAccessAuditRowSchema),
});
export const ThreadAuditResponseSchema = z.object({
    threadId: z.string(),
    threadOwnerUserId: z.string().uuid(),
    entries: z.array(EmailAccessAuditRowSchema),
});
// =============================================================================
// Meeting access audit (mirrors EmailAccessAudit for cross-rep meeting access)
// =============================================================================
export const MeetingAccessTypeSchema = z.enum([
    'list',
    'detail',
    'transcript',
    'summary',
    'coaching_view',
    'share_grant',
    'share_revoke',
    'audit_view',
    'break_glass',
]);
export const MeetingAccessAuditRowSchema = z.object({
    id: z.string(),
    actorUserId: z.string().uuid(),
    actorName: z.string().nullable(),
    actorRole: z.string().nullable(),
    actorAvatarFileId: z.string().nullable().optional(),
    targetUserId: z.string().uuid(),
    targetName: z.string().nullable().optional(),
    targetRole: z.string().nullable().optional(),
    meetingId: z.string().nullable(),
    meetingTitle: z.string().nullable().optional(),
    accessType: MeetingAccessTypeSchema,
    accessReason: z.string().nullable().optional(),
    accessedAt: z.string(),
});
export const MeetingAccessAuditListResponseSchema = z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
    entries: z.array(MeetingAccessAuditRowSchema),
});
// =============================================================================
// Rep self-view rollup (GDPR Article 15)
// =============================================================================
export const EmailAccessSummaryAccessorSchema = z.object({
    userId: z.string().uuid(),
    name: z.string().nullable(),
    role: z.string().nullable(),
    count: z.number(),
    avatarUrl: z.string().nullable(),
    latestAccessedAt: z.string(),
    accessTypeCounts: z.record(z.string(), z.number()),
    reasons: z.array(z.string()),
});
export const EmailAccessSummarySchema = z.object({
    last30Days: z.object({
        totalAccesses: z.number(),
        byAccessor: z.array(EmailAccessSummaryAccessorSchema),
    }),
});
// =============================================================================
// Admin email metrics list response (aggregate, no content)
// =============================================================================
export const AdminEmailMetricsRowSchema = z.object({
    userId: z.string().uuid(),
    userName: z.string().nullable(),
    emailsSent: z.number(),
    emailsReceived: z.number(),
    avgResponseMs: z.number().nullable(),
});
export const AdminEmailMetricsListResponseSchema = z.object({
    rows: z.array(AdminEmailMetricsRowSchema),
    total: z.number(),
});
// =============================================================================
// Activity Audit (tamper-evident org-wide audit log)
// =============================================================================
export const AuditActionSchema = z.enum([
    'view',
    'create',
    'update',
    'delete',
    'export',
    'login',
    'login_failed',
    'logout',
    'permission_change',
    'role_change',
    'feature_toggle',
    'provision',
    'ai_chat',
    'ai_suggestion_used',
    'download',
    'search',
    'audit_view',
    'session_revoke',
    'delete_hardened',
    'delete_cancelled',
    // restore: a soft-deleted client (and its cascaded deals) was restored from the recycle bin.
    'restore',
    'primary_contact_change',
    'data_export',
    'audit_partition_archived',
    // Memory subsystem (Section B):
    // - erase_memory: GDPR Art. 17 / SOC2 CC6.5 - proof of memory deletion
    //   for a meeting / client / deal / rep / org.
    // - view_rep_memory: SOC2 CC6.1 - admin viewed another rep's coaching profile.
    // - list_rep_memory: SOC2 CC6.1 - admin listed all reps' coaching profiles.
    'erase_memory',
    'view_rep_memory',
    'list_rep_memory',
    // All-clients / All-deals read-only browse mode (Wave 1):
    // - view_all_clients_scope / view_all_deals_scope: requester invoked the
    //   list endpoint with `view=all` and the scope expanded beyond their
    //   collab set (logged once per request with `metadata.count`).
    // - view_non_collab_client / view_non_collab_deal: requester opened a
    //   single-resource detail route for a client/deal they don't collab on.
    // - view_non_collab_client_deals: requester listed a client's deals where
    //   they collaborate on none of them.
    // - view_non_collab_client_insights / view_non_collab_deal_insights:
    //   account / deal insights served at expanded scope or read-only access.
    // - denied_non_collab_write: chat or REST write tool refused because the
    //   requester is not a collaborator on the target resource.
    'view_all_clients_scope',
    'view_all_deals_scope',
    'view_all_action_items_scope',
    'view_non_collab_client',
    'view_non_collab_deal',
    'view_non_collab_client_deals',
    'view_non_collab_client_insights',
    'view_non_collab_deal_insights',
    'denied_non_collab_write',
    'archive',
    // A file was evaluated by the Revont security pipeline. The result and
    // policy findings are stored in metadata on the file resource row.
    'scan',
]);
export const AuditResourceTypeSchema = z.enum([
    'deal',
    'client',
    'contact',
    'meeting',
    'email',
    'action_item',
    'note',
    'kb_article',
    'user',
    'org',
    'auth',
    'ai',
    'file',
    'capability',
    'search',
    // Memory subsystem (Section B): rep coaching profile resource. Distinct from
    // 'user' so audit consumers can filter memory-specific events without picking
    // up unrelated provisioning / role-change rows.
    'rep',
    'custom_field',
]);
export const ActivityAuditRowSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string(),
    actorUserId: z.string().uuid(),
    actorRole: RoleSchema,
    action: AuditActionSchema,
    resourceType: AuditResourceTypeSchema,
    resourceId: z.string().nullable(),
    resourceLabel: z.string().nullable(),
    targetUserId: z.string().uuid().nullable(),
    metadata: z.record(z.string(), z.unknown()),
    prevHash: z.string().nullable(),
    rowHash: z.string(),
    occurredAt: z.string(),
    // Optional joined user fields - populated by list() endpoints that LEFT JOIN
    // the users shadow table so the UI can render names + avatars instead of
    // raw UUIDs. Inserts and the hash chain do NOT include these fields.
    actorName: z.string().nullable().optional(),
    actorAvatarFileId: z.string().nullable().optional(),
    actorEmail: z.string().nullable().optional(),
    targetName: z.string().nullable().optional(),
    targetAvatarFileId: z.string().nullable().optional(),
    targetEmail: z.string().nullable().optional(),
});
export const ListActivityAuditQuerySchema = z.object({
    actorUserId: z.string().uuid().optional(),
    resourceType: AuditResourceTypeSchema.optional(),
    action: AuditActionSchema.optional(),
    targetUserId: z.string().uuid().optional(),
    from: z.string().optional(),
    to: z.string().optional(),
    q: z.string().optional(),
    limit: z.number().optional(),
    offset: z.number().optional(),
    excludeAuditView: z
        .union([z.boolean(), z.string()])
        .optional()
        .transform((v) => (typeof v === 'string' ? v === 'true' : v)),
});
export const ListActivityAuditResponseSchema = z.object({
    rows: z.array(ActivityAuditRowSchema),
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
});
